import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package, DollarSign, Save, X, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../lib/toast';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';

const ExtrasManager = () => {
  const { api } = useAuth();
  const [groups, setGroups] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    extras: [{ name: '', price: 0, supply_id: '', quantity_to_deduct: 1 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, suppliesRes] = await Promise.all([
        api.get('/extras/groups'),
        api.get('/supplies')
      ]);
      setGroups(groupsRes.data || []);
      setSupplies(suppliesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExtra = () => {
    setFormData({
      ...formData,
      extras: [...formData.extras, { name: '', price: 0, supply_id: '', quantity_to_deduct: 1 }]
    });
  };

  const handleRemoveExtra = (index) => {
    if (formData.extras.length > 1) {
      const newExtras = formData.extras.filter((_, i) => i !== index);
      setFormData({ ...formData, extras: newExtras });
    }
  };

  const handleExtraChange = (index, field, value) => {
    const newExtras = [...formData.extras];
    newExtras[index][field] = field === 'price' || field === 'quantity_to_deduct' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, extras: newExtras });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre del grupo es requerido');
      return;
    }
    
    const validExtras = formData.extras.filter(e => e.name.trim());
    if (validExtras.length === 0) {
      toast.error('Agrega al menos un extra');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        extras: validExtras,
        is_active: true
      };

      if (editingGroup) {
        await api.put(`/extras/groups/${editingGroup.group_id}`, payload);
        toast.success('Grupo actualizado');
      } else {
        await api.post('/extras/groups', payload);
        toast.success('Grupo creado');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Error al guardar');
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      extras: group.extras.length > 0 ? group.extras : [{ name: '', price: 0, supply_id: '', quantity_to_deduct: 1 }]
    });
    setShowForm(true);
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('¿Eliminar este grupo de extras?')) return;
    
    try {
      await api.delete(`/extras/groups/${groupId}`);
      toast.success('Grupo eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      extras: [{ name: '', price: 0, supply_id: '', quantity_to_deduct: 1 }]
    });
  };

  const toggleExpand = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </main>
      </div>
    );
  }

  const content = (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Layers className="w-7 h-7 text-orange-500" />
            Grupos de Extras
          </h1>
          <p className="text-gray-500 text-sm mt-1">Crea grupos de extras y asígnalos a tus productos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          data-testid="create-extra-group-btn"
        >
          <Plus className="w-5 h-5" />
          Nuevo Grupo
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo de Extras'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Group Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Grupo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Complementos Mariscos"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="extra-group-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ej: Extras para platillos de mariscos"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Extras List */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Extras en este grupo
                  </label>
                  <button
                    type="button"
                    onClick={handleAddExtra}
                    className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Agregar Extra
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.extras.map((extra, index) => (
                    <div key={index} className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg items-end">
                      <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs text-gray-500 mb-1">Nombre</label>
                        <input
                          type="text"
                          value={extra.name}
                          onChange={(e) => handleExtraChange(index, 'name', e.target.value)}
                          placeholder="Ej: Limón"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          data-testid={`extra-name-input-${index}`}
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs text-gray-500 mb-1">Precio</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                          <input
                            type="number"
                            value={extra.price}
                            onChange={(e) => handleExtraChange(index, 'price', e.target.value)}
                            className="w-full pl-6 pr-2 py-2 border rounded-lg text-sm"
                            min="0"
                            step="0.01"
                            data-testid={`extra-price-input-${index}`}
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs text-gray-500 mb-1">Insumo (inventario)</label>
                        <select
                          value={extra.supply_id || ''}
                          onChange={(e) => handleExtraChange(index, 'supply_id', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        >
                          <option value="">Sin vincular</option>
                          {supplies.map(s => (
                            <option key={s.supply_id} value={s.supply_id}>
                              {s.name} ({s.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-20">
                        <label className="block text-xs text-gray-500 mb-1">Cantidad</label>
                        <input
                          type="number"
                          value={extra.quantity_to_deduct}
                          onChange={(e) => handleExtraChange(index, 'quantity_to_deduct', e.target.value)}
                          className="w-full px-2 py-2 border rounded-lg text-sm"
                          min="0"
                          step="0.1"
                        />
                      </div>
                      {formData.extras.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveExtra(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
                  data-testid="save-extra-group-btn"
                >
                  <Save className="w-4 h-4" />
                  {editingGroup ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl">
          <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No hay grupos de extras</h3>
          <p className="text-gray-400 mb-4">Crea tu primer grupo para agregar extras a tus productos</p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Crear Grupo
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {groups.map(group => (
            <div
              key={group.group_id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              data-testid={`extra-group-${group.group_id}`}
            >
              {/* Group Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(group.group_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{group.name}</h3>
                    <p className="text-sm text-gray-500">
                      {group.extras?.length || 0} extras
                      {group.description && ` • ${group.description}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(group); }}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                    data-testid={`edit-group-${group.group_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(group.group_id); }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-group-${group.group_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedGroups[group.group_id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Extras List (Expanded) */}
              {expandedGroups[group.group_id] && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="grid gap-2">
                    {group.extras?.map((extra, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-white rounded-lg"
                      >
                        <span className="font-medium text-gray-700">{extra.name}</span>
                        <div className="flex items-center gap-4">
                          {extra.supply_id && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                              Vinculado a inventario
                            </span>
                          )}
                          <span className="font-bold text-green-600 flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {extra.price?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        {content}
      </main>
    </div>
  );
};

export default ExtrasManager;
